/**
 * The crop, checked at the table before anything goes up: the photo just taken with the corners
 * the phone found (or the whole photo), adjustable by dragging. "Use this crop" cuts the receipt
 * out again on the phone (lib/scan/photo.ts recutPhoto); nothing is sent until the receipt is saved.
 */
import { Maximize, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CropFrame } from "@/components/capture/crop-frame";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ScanCorner } from "@/lib/api/documents";
import { cropState } from "@/lib/capture/crop";
import { recutPhoto, type ScannedPhoto } from "@/lib/scan/photo";

export function CaptureCropDialog({
  photo,
  number,
  onApply,
  onClose,
}: {
  /** The photo being checked; null closes the dialog. */
  photo: ScannedPhoto | null;
  number: number;
  onApply: (photo: ScannedPhoto) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={photo !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="capture-crop">
        <DialogHeader>
          <DialogTitle>
            {t("crop.title")} · {t("capture.page", { number })}
          </DialogTitle>
          <DialogDescription>{t("capture.cropHint")}</DialogDescription>
        </DialogHeader>
        {/* Keyed by the scan, so each photo starts from the corners it was cut along. */}
        {photo && (
          <CropBody
            key={`${number}-${photo.scan.lastModified}`}
            photo={photo}
            onApply={onApply}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CropBody({
  photo,
  onApply,
  onClose,
}: {
  photo: ScannedPhoto;
  onApply: (photo: ScannedPhoto) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [corners, setCorners] = useState<ScanCorner[] | null>(photo.page.corners);
  const [applying, setApplying] = useState(false);
  // What the corners on screen are: as found, moved by hand, or the whole photo.
  const state = cropState({
    ...photo.page,
    corners,
    detected: photo.page.detected && corners === photo.page.corners,
  });

  const apply = async () => {
    if (corners === photo.page.corners) return onClose();
    setApplying(true);
    try {
      onApply(await recutPhoto(photo, corners));
    } catch {
      toast.error(t("capture.unreadablePhoto"));
      setApplying(false);
    }
  };

  return (
    <>
      <p className="text-xs text-muted-foreground" data-testid="crop-state">
        {t(`crop.state.${state}`)}
      </p>
      <CropFrame url={photo.original} page={photo.page} corners={corners} onChange={setCorners} />
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCorners(null)}
          data-testid="crop-whole"
        >
          <Maximize /> {t("crop.whole")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setCorners(photo.page.corners)}>
          <RotateCcw /> {t("crop.reset")}
        </Button>
      </div>
      <DialogFooter>
        <Button onClick={() => void apply()} disabled={applying} data-testid="crop-apply">
          {applying ? t("capture.scanning") : t("capture.cropApply")}
        </Button>
      </DialogFooter>
    </>
  );
}
